<template>
  <div v-if="cart && superUserMyCart" class="superuser-cart-actions">
    <slot name="activator" :open="open">
      <oie-base-action
        :disabled="$attrs.disabled"
        class="superuser-cart-actions__activator"
        theme="outlined"
        @click="open">
        {{ tStatic('superUserCartHandling') }}
      </oie-base-action>
    </slot>
    <oie-portal-wrapper :name="id" class="superuser-cart-actions-selector__slide-in">
      <template #title>
        <h2>{{ tStatic('superUserCartHandling') }}</h2>
      </template>
      <div class="superuser-cart-actions-selector__content superuser-cart-actions-merge-cart__actions">
        <oie-base-dropdown
          v-model="action"
          :options="actions"
          :label-text="tStatic('superUserCartHandling')"
          container-classes="order-request__dropdown">
        </oie-base-dropdown>

        <oie-base-dropdown
          v-model="sourceCart"
          :options="sourceCarts"
          :default-selected="createDefaultSourceSelection"
          :label-text="tStatic('superUserCartFromCart')"
          container-classes="order-request__dropdown">
        </oie-base-dropdown>

        <oie-base-dropdown
          v-model="targetCart"
          :options="targetCarts"
          :default-selected="createDefaultTargetSelection"
          :label-text="tStatic('superUserCartToCart')"
          container-classes="order-request__dropdown">
        </oie-base-dropdown>

        <oie-base-button
          :text="tStatic('superUserCartExecute')"
          css-classes="button--ghost button--ghost-green"
          @click="performCartAction">
        </oie-base-button>

        <template v-if="preview && preview.rows && preview.rows.length > 0">
          <h3>{{ tStatic('superUserCartPreview') }}</h3>
          <div v-element-loader="{ endpoint: '/superuser/cart/preview' }">
            <oie-superuser-cart-product-item
              v-for="cartRow of preview.rows"
              :key="cartRow.id"
              :cart-row="cartRow"
              :cart-name="cart.name">
            </oie-superuser-cart-product-item>
          </div>
        </template>
      </div>
    </oie-portal-wrapper>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'

export default {
  data() {
    return {
      actions: [
        { value: 'merge', text: this.tStatic('superUserCartActionMerge') },
        { value: 'replace', text: this.tStatic('superUserCartActionReplace') }
      ],
      sourceCarts: [
        { value: 'superuser', text: this.tStatic('superUserCartTypeSuperUser') },
        { value: 'customer', text: this.tStatic('superUserCartTypeCustomer') },
        { value: 'mine', text: this.tStatic('superUserCartTypeMine') }
      ],
      action: 'replace',
      sourceCart: null,
      targetCart: null,
      preview: null
    }
  },
  computed: {
    ...mapGetters('commerce', ['cart', 'superUserMyCart']),
    id() {
      return _.uniqueId('superuser-cart-actions-selector-')
    },
    currentCartName() {
      return this.activeCart.isSuperUserCart ? this.tStatic('superUserCartTypeSuperUser') : this.tStatic('superUserCartTypeCustomer')
    },
    activeCart() {
      return this.cart
    },
    createDefaultSourceSelection() {
      return this.superUserMyCart && this.superUserMyCart.rows && this.superUserMyCart.rows.length > 0
        ? 'mine'
        : this.activeCart.isSuperUserCart ? 'superuser' : 'customer'
    },
    createDefaultTargetSelection() {
      return this.sourceCart === 'superuser'
        ? 'customer'
        : this.sourceCart === 'customer'
          ? 'superuser'
          : this.sourceCart === 'mine'
            ? this.activeCart.isSuperUserCart
              ? 'superuser'
              : 'customer'
            : 'customer'
    },
    targetCarts() {
      const allTargets = [
        { value: 'superuser', text: this.tStatic('superUserCartTypeSuperUser') },
        { value: 'customer', text: this.tStatic('superUserCartTypeCustomer') },
        { value: 'mine', text: this.tStatic('superUserCartTypeMine') }
      ]
      return allTargets.filter(opt => opt.value !== this.sourceCart)
    }
  },
  watch: {
    action: 'getPreviewCart',
    sourceCart() {
      const validTargets = this.targetCarts.map(opt => opt.value)
      if (!validTargets.includes(this.targetCart)) {
        this.targetCart = validTargets[0] || null
      }
      this.getPreviewCart()
    },
    targetCart: 'getPreviewCart'
  },
  mounted() {
    if (this.clientModel.impersonating !== null && !this.clientModel.impersonating.anonymous) {
      this.getSuperUserMyCart()
    }
  },
  methods: {
    ...mapActions('ui', ['setActiveSlideIn']),
    ...mapActions('commerce', ['getSuperUserMyCart', 'getCart']),
    async open() {
      this.setActiveSlideIn(this.id)
      if (this.preview !== null) {
        this.getPreviewCart()
      }
    },
    getPreviewCart() {
      if (!this.action || !this.sourceCart || !this.targetCart) {
        this.preview = null
        return
      }

      httpService.post('superuser/cart/preview', {
        action: this.action,
        sourceCart: this.sourceCart,
        targetCart: this.targetCart
      }).then(({ data }) => {
        this.preview = data.data
      }).catch((e) => {
      })
    },
    async performCartAction() {
      if (!this.action || !this.sourceCart || !this.targetCart) {
        return
      }

      await httpService.post('superuser/cart/performCartAction', {
        action: this.action,
        sourceCart: this.sourceCart,
        targetCart: this.targetCart
      }).then(() => {
        this.setActiveSlideIn(null)
        this.getCart(this.clientModel.cartType)
        this.getSuperUserMyCart()
      }).catch((e) => {
      })
    },
    clickedOutside() {
      this.preview = null
      this.setActiveSlideIn(null)
    }
  }
}
</script>

<style lang="scss">
.superuser-cart-actions__activator {
    width: 100%;
}

.superuser-cart-actions-selector__content >*:not(:last-child) {
  margin-bottom: measure(half);
}
.superuser-cart-actions-merge-cart__actions > button {
  width: 100%;
  margin-bottom: measure(quarter);

  &:last-child {
    margin-bottom: 0;
  }
}

</style>
